import dayjs from 'dayjs';
import { defineStore } from 'pinia';
import { ref, watch } from 'vue';

import { isDatadogSyntheticsBrowser } from '@/config';
import { env } from '@/env';
import { useFlagStore } from '@/stores/flag';
import { useIdentityStore } from '@/stores/identity';
import { useTrackingStore } from '@/stores/tracking';
import { loadScript } from '@/utils/dom';
import { logger } from '@/utils/logger';

declare global {
  interface Window {
    Cypress?: unknown;
    _sva?: {
      traits?: Record<string, unknown>;
      setVisitorTraits?: (traits: Record<string, unknown>) => void;
      // Re-run the targeting pass; a SPA has no full reload, so audiences never re-evaluate without it.
      retarget?: () => void;
      // Survicate's web SDK passes the survey id as a bare string; `answer` is question_answered-only, unused here (widget_wrapper.d.ts).
      addEventListener?: (
        eventName: string,
        callback: (surveyId?: string, answer?: unknown) => void,
      ) => void;
    };
  }
}

type SurvicateTraits = Record<string, string | number | undefined>;

export const useSurvicateStore = defineStore('survicate', () => {
  const flagStore = useFlagStore();
  const identityStore = useIdentityStore();
  const trackingStore = useTrackingStore();
  const ready = ref(false);
  const initCalled = ref(false);
  const initializing = ref(false);
  let listenersRegistered = false;
  let survicateReadyBound = false;
  let traitsIncompleteTracked = false;
  const trackedSkipReasons = new Set<string>();

  const workspaceKey = env.survicateWorkspaceKey;
  const enabled = env.survicateEnabled && !isDatadogSyntheticsBrowser;

  // Track missing targeting traits once per session; caller gates on a hydrated identity.
  function trackMissingTraits(missing: string[]): void {
    if (traitsIncompleteTracked || missing.length === 0) {
      return;
    }
    traitsIncompleteTracked = true;
    trackingStore.track('Survey Traits Incomplete', { missing: missing.join(',') });
  }

  // Why init() won't show the survey, for telemetry; null = non-decision (unhydrated/tests/synthetics).
  function resolveSkipReason(): string | null {
    // Only attribute to a hydrated identity; a pre-identify skip is "not ready yet", not a decision.
    if (identityStore.identity?.id == null) return null;
    if (window.Cypress || isDatadogSyntheticsBrowser) return null;
    if (!env.survicateEnabled) return 'disabled';
    // Flags load async; a pre-Ready read is falsy for every flag, so wait before calling it flag_off.
    if (!flagStore.ready) return null;
    if (!flagStore.flags.surveySurvicate) return 'flag_off';
    return null;
  }

  // Track a skip decision once per reason per session (the retry watcher re-enters init repeatedly).
  function trackSkip(): void {
    const reason = resolveSkipReason();
    if (!reason || trackedSkipReasons.has(reason)) return;
    trackedSkipReasons.add(reason);
    trackingStore.track('Survey Skipped', { reason });
    logger.info('[Survicate] Survey init skipped', { reason });
  }

  function buildTraits(): SurvicateTraits | null {
    const identity = identityStore.identity;
    const currentBrand = identityStore.currentBrand;
    if (!identity) return null;

    const missingTraits: string[] = [];

    // Omit tenure traits when dates missing; a -1 sentinel would mis-target Survicate audiences.
    let daysAsCustomer: number | undefined;
    if (identity?.created_at) {
      const userCreatedAt = dayjs(identity.created_at);
      daysAsCustomer = dayjs().diff(userCreatedAt, 'd');
    } else {
      missingTraits.push('daysAsCustomer');
      logger.warn(
        '[Survicate] identity.created_at is missing — daysAsCustomer omitted from traits',
      );
    }

    const plan = currentBrand?.plan;
    let currentBrandDaysAsCustomer: number | undefined;
    if (plan?.created_at) {
      const planCreatedAt = dayjs(plan.created_at);
      currentBrandDaysAsCustomer = dayjs().diff(planCreatedAt, 'd');
    } else if (currentBrand?.isActive) {
      // Inactive brands use InactivePlan, which has no created_at by contract — expected, don't flag.
      missingTraits.push('currentBrandDaysAsCustomer');
      logger.warn(
        '[Survicate] currentBrand.plan.created_at is missing — currentBrandDaysAsCustomer omitted, survey audience may not match',
      );
    }
    if (!plan?.customer_stage) {
      missingTraits.push('currentBrandPlanCustomerStage');
      logger.warn(
        '[Survicate] currentBrand.plan.customer_stage is missing — survey audience may not match',
      );
    }

    // Skip pre-identify stub fires (no id = anonymous distinct_id); re-runs once identity lands.
    if (identity.id != null) {
      trackMissingTraits(missingTraits);
    }

    return {
      // Stable identifier so Survicate ties responses to the dash user (dedupe, per-user frequency caps).
      user_id: String(identity.id),
      email: identity.email,
      firstName: identity.first_name,
      lastName: identity.last_name,
      jobTitle: identity.job_title,
      daysAsCustomer,
      organizationName: identity.organization?.name,
      organizationCustomerStage: identity.organization?.customer_stage,
      currentBrandName: currentBrand?.name,
      currentBrandDaysAsCustomer,
      currentBrandPlanType: currentBrand?.plan?.plan_type,
      currentBrandPlanCustomerStage: plan?.customer_stage,
    };
  }

  // Audience-targeting traits only (no PII/identifiers) — the attributes that gate a survey.
  function targetingTraits(traits: SurvicateTraits | null): SurvicateTraits {
    if (!traits) return {};
    const {
      daysAsCustomer,
      organizationName,
      organizationCustomerStage,
      currentBrandName,
      currentBrandDaysAsCustomer,
      currentBrandPlanType,
      currentBrandPlanCustomerStage,
    } = traits;
    return {
      daysAsCustomer,
      organizationName,
      organizationCustomerStage,
      currentBrandName,
      currentBrandDaysAsCustomer,
      currentBrandPlanType,
      currentBrandPlanCustomerStage,
    };
  }

  function setTraits(traits: SurvicateTraits): void {
    window._sva = window._sva ?? {};
    window._sva.traits = traits;
    if (typeof window._sva?.setVisitorTraits === 'function') {
      window._sva.setVisitorTraits(traits);
    }
  }

  // On live `_sva`: bind listeners, push traits, retarget. Defer to window `SurvicateReady` if not up yet.
  function handleSurvicateReady(): void {
    if (typeof window._sva?.addEventListener !== 'function') {
      if (!survicateReadyBound) {
        survicateReadyBound = true;
        window.addEventListener('SurvicateReady', () => handleSurvicateReady(), { once: true });
      }
      return;
    }
    if (!listenersRegistered) {
      listenersRegistered = true;
      // Survicate passes the survey id directly as a string, so record it as-is.
      window._sva.addEventListener('survey_displayed', (surveyId) => {
        trackingStore.track('Survey Displayed', { surveyId });
      });
      window._sva.addEventListener('survey_completed', (surveyId) => {
        trackingStore.track('Survey Complete', { surveyId });
      });
    }
    // Push traits via the real API (absent at onload) and retarget so surveys can match.
    const traits = buildTraits();
    if (traits && typeof window._sva.setVisitorTraits === 'function') {
      window._sva.setVisitorTraits(traits);
    }
    window._sva.retarget?.();
  }

  function init(): void {
    initCalled.value = true;
    if (
      !window.Cypress &&
      enabled &&
      // Datadog flag 'survey-survicate' (kebabCase of surveySurvicate; literal kept so flag scanners/staleness see it)
      flagStore.flags.surveySurvicate &&
      // Wait for a hydrated identity so distinct_id/traits are real, not pre-identify. Retried below.
      identityStore.identity?.id != null &&
      !ready.value &&
      !initializing.value
    ) {
      initializing.value = true;

      // Don't pre-seed `window._sva`; the loader owns it and pre-boot traits are dropped.
      handleSurvicateReady();

      const scriptUrl = `https://survey.survicate.com/workspaces/${workspaceKey}/web_surveys.js`;
      loadScript(scriptUrl, {
        id: 'survicate-widget',
        onload: () => {
          ready.value = true;
          handleSurvicateReady();
          // Handoff to Survicate; targeting traits attached for Mixpanel segmentation.
          trackingStore.track('Survey Eligible', {
            workspaceKey,
            ...targetingTraits(buildTraits()),
          });
          logger.info('[Survicate] Survey script loaded', { workspaceKey });
        },
        onerror: () => {
          initializing.value = false;
          logger.error('[Survicate] Failed to load survey script', { workspaceKey, scriptUrl });
        },
      });
    } else {
      trackSkip();
    }
  }

  // Retry init() when the gating inputs land: flag flips on, or identity finishes hydrating.
  watch(
    () => ({
      flag: flagStore.flags.surveySurvicate,
      identityId: identityStore.identity?.id,
    }),
    () => {
      if (initCalled.value && !initializing.value) {
        init();
      }
    },
  );

  // Update traits when identity or brand changes after script is loaded
  watch(
    () => ({
      identity: identityStore.identity,
      currentBrand: identityStore.currentBrand,
      ready: ready.value,
    }),
    (value) => {
      if (value.ready && value.identity) {
        const traits = buildTraits();
        if (traits) {
          setTraits(traits);
        }
        // Re-run targeting so the updated traits (e.g. after a brand switch) can match audiences.
        window._sva?.retarget?.();
      }
    },
    {
      immediate: true,
    },
  );

  // Re-run Survicate targeting on demand (e.g. SPA route change). No-op until the script is ready.
  function retarget(): void {
    if (ready.value) {
      window._sva?.retarget?.();
    }
  }

  return {
    init,
    ready,
    retarget,
  };
});
